import cx from 'classnames'
import { Carousel } from 'common/Carousel/Carousel'
import { Modal } from 'common/Modal/Modal'
import { updateCardStyle } from 'common/Prose/Commands/updateCardStyle'
import { CardViewComponent } from 'common/Prose/nodeViews/Card/components/CardViewComponent/CardViewComponent'
import {
  backgroundOptions,
  fontOptions,
  layoutOptions,
} from 'common/Prose/nodeViews/Card/components/CardViewComponent/customizationConstants'
import { MentionSubject } from 'common/Prose/utils/smartLinks/types'
import { CardLayout, cardViewStyle } from 'prose-core'
import { type EditorView } from 'prosemirror-view'
import React, { useMemo, useState } from 'react'
import styles from './CustomizeCardModal.module.scss'

export type CustomizeModalProps = {
  isOpen: boolean
  activeStyle: cardViewStyle
  onClose: () => void
  getPos: () => number
  view: EditorView
  mentionSubject: MentionSubject
  mentionType: 'User' | 'Article'
  subtitle: string
}

export const CustomizeCardModal: React.FC<React.PropsWithChildren<CustomizeModalProps>> = (props) => {
  const { isOpen, onClose, view, getPos, activeStyle, mentionSubject, mentionType, subtitle } = props
  const activeBackground = activeStyle.background ?? layoutOptions[0].background
  const activeFont = activeStyle.font ?? layoutOptions[0].font
  const activeLayout = activeStyle.layout ?? layoutOptions[0].layout
  const [selectedBackground, setSelectedBackground] = useState<string>(activeBackground)
  const [selectedFont, setSelectedFont] = useState<string>(activeFont)
  const [selectedLayout, setSelectedLayout] = useState<CardLayout>(activeLayout)

  const selectBackground = (background: string) => {
    setSelectedBackground(background)
  }

  const selectFont = (font: string) => {
    setSelectedFont(font)
  }

  const changeLayoutAndStyles = (index: number) => {
    const selectedLayoutOption = layoutOptions[index]
    setSelectedLayout(selectedLayoutOption.layout)
    setSelectedFont(selectedLayoutOption.font)
    setSelectedBackground(selectedLayoutOption.background)
  }

  const handleStyleUpdate = () => {
    const newStyle: cardViewStyle = { font: selectedFont, background: selectedBackground, layout: selectedLayout }
    updateCardStyle(getPos(), newStyle)(view.state, view.dispatch)
    onClose()
  }

  const onCancel = () => {
    setSelectedBackground(activeBackground)
    setSelectedFont(activeFont)
    setSelectedLayout(activeLayout)
    onClose()
  }

  const activeIndex = useMemo(() => {
    return layoutOptions.findIndex((option) => option.layout === selectedLayout)
  }, [selectedLayout])

  return (
    <Modal
      wrapperClassName={styles.customizeModalWrapper}
      bodyClassName={styles.customizeModalBody}
      show={isOpen}
      onClose={onCancel}
      title={'Pick Card Style'}
      footerChildren={
        <div className={styles.buttonsContainer}>
          <button onClick={handleStyleUpdate}>Update Style</button>
        </div>
      }
    >
      <div className={styles.top}>
        <Carousel activeIndex={activeIndex} afterChange={changeLayoutAndStyles} slideCount={layoutOptions.length}>
          {layoutOptions.map((option, index) => {
            const isSelected = index === activeIndex
            const activeOption = isSelected
              ? { background: selectedBackground, font: selectedFont, layout: selectedLayout }
              : option
            return (
              <div
                key={option.layout}
                className={cx(styles.carouselItem, { [styles.selected]: isSelected })}
                onClickCapture={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                }}
              >
                <CardViewComponent
                  disabled={true}
                  selected={false}
                  comments={[]}
                  tasks={[]}
                  mentionSubject={mentionSubject}
                  mentionType={mentionType}
                  subtitle={subtitle}
                  url=""
                  cardStyle={activeOption}
                />
              </div>
            )
          })}
        </Carousel>
      </div>
      <div className={styles.bottom}>
        <div className={styles.stylePicker}>
          <div className={styles.background}>
            <h2 className={styles.label}>Background</h2>
            <div className={styles.backgroundPicker}>
              {backgroundOptions.map((option) => {
                const background = option.background
                return (
                  <div
                    key={background}
                    className={cx(styles.backgroundOption, { [styles.selected]: selectedBackground === background })}
                    style={{ background }}
                    onClick={() => selectBackground(background)}
                  ></div>
                )
              })}
            </div>
          </div>
          <div className={styles.font}>
            <h2 className={styles.label}>Font</h2>
            <div className={styles.fontPicker}>
              {fontOptions.map((option) => (
                <div
                  key={option.value}
                  className={cx(styles.fontOption, { [styles.selected]: selectedFont === option.value })}
                  style={{ fontFamily: option.value }}
                  onClick={() => selectFont(option.value)}
                >
                  {option.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
